import { useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import {
  markAICheckerEmailKnown,
  setAICheckerQuantityFlagKnown,
  type AICheckerTrace,
  type FlaggedProductWithRestaurant,
} from '../../../../../../services/admin/adminService';
import { BookmarkCheck, Star } from 'lucide-react';
import { KnownCategoryModal } from './KnownCategoryModal';
import type { TFn } from '../../types';

/**
 * "Mark known" — file this unprocessed email as an acknowledged-but-unfixed to-do under a category.
 * Flips ONLY this row out of the issue queue and stores the chosen category on it; no other email is
 * affected now or later. Counts tracked per category in the Known to-dos panel.
 */
export function MarkKnownButton({ trace, onChanged, t }: { trace: AICheckerTrace; onChanged?: () => void; t: TFn }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title={t('adminTesting.review.markKnownTitle')}
        className={cn('p-1 rounded-md hover:bg-amber-100 dark:hover:bg-amber-900/30 disabled:opacity-50', 'text-amber-600 dark:text-amber-400')}
      >
        <BookmarkCheck className="w-3.5 h-3.5" />
      </button>
      {open && (
        <KnownCategoryModal
          title={t('adminTesting.review.markKnownModalTitle')}
          subtitle={t('adminTesting.review.markKnownModalSubtitle')}
          onConfirm={async (category) => {
            await markAICheckerEmailKnown(trace.id, category);
            onChanged?.();
          }}
          onClose={() => setOpen(false)}
          t={t}
        />
      )}
    </>
  );
}

/**
 * "Mark known" for ONE quantity-info flag — files it under a to-do category instead of marking it done,
 * removing it from the open flag list. Counts tracked per category in the Known to-dos panel.
 * `duplicates` = every flag a de-duplicated row stands for; all of them get filed.
 */
export function QtyFlagKnownButton({ flag, duplicates, onDone, t }: { flag: FlaggedProductWithRestaurant; duplicates?: FlaggedProductWithRestaurant[]; onDone: () => void; t: TFn }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title={t('adminTesting.review.markKnownQtyTitle')}
        className={cn('p-1 rounded-md transition-colors text-gray-500 dark:text-gray-400',
          'hover:bg-amber-100 dark:hover:bg-amber-900/30 hover:text-amber-600 dark:hover:text-amber-400')}
      >
        <Star className="w-4 h-4" />
      </button>
      {open && (
        <KnownCategoryModal
          title={t('adminTesting.review.markKnownQtyModalTitle')}
          subtitle={flag.productName}
          onConfirm={async (category) => {
            for (const d of duplicates ?? [flag]) {
              await setAICheckerQuantityFlagKnown(d.restaurantId, d.articleId, d.productName, category, d.source ?? 'pakbon');
            }
            onDone();
          }}
          onClose={() => setOpen(false)}
          t={t}
        />
      )}
    </>
  );
}
